import crypto from 'crypto-js'
import { Base64 } from 'js-base64'
import { sm4 } from 'sm-crypto-v2'
import { arrayBufferToBase64 } from './util'

/**
 * SM4-CBC加密工具类
 */
export class SM4CbcUtils {
  // 128位密钥长度（16字节）
  private static readonly KEY_LENGTH = 16
  // 16字节IV
  private static readonly IV_LENGTH = 16

  /**
   * 生成SM4密钥
   * @returns 16字节的随机密钥（十六进制字符串）
   */
  public static generateKey(): string {
    const wordArray = crypto.lib.WordArray.random(this.KEY_LENGTH)
    return wordArray.toString(crypto.enc.Hex)
  }

  /**
   * 生成IV
   * @returns 16字节的随机IV（十六进制字符串）
   */
  public static generateIV(): string {
    const wordArray = crypto.lib.WordArray.random(this.IV_LENGTH)
    return wordArray.toString(crypto.enc.Hex)
  }

  /**
   * 加密数据，返回base64编码的加密结果
   * @param data 待加密的字符串
   * @param key 密钥（十六进制字符串）
   * @param iv IV（十六进制字符串）
   * @returns base64编码的加密结果
   */
  public static encryptToBase64(data: string | Uint8Array, key: string, iv: string): string {
    try {
      const encryptedData = this.encrypt(data, key, iv)
      return arrayBufferToBase64(encryptedData as Uint8Array)
    }
    catch (error) {
      console.error('SM4-CBC加密失败', error)
      throw new Error('SM4-CBC加密失败')
    }
  }

  /**
   * 解密base64编码的加密数据
   * @param encryptedDataBase64 base64编码的加密数据
   * @param key 密钥（十六进制字符串）
   * @param iv IV（十六进制字符串）
   * @returns 解密后的字符串
   */
  public static decryptFromBase64(encryptedDataBase64: string, key: string, iv: string): string {
    try {
      const encryptedData = Base64.decode(encryptedDataBase64)
      return this.decrypt(encryptedData, key, iv)
    }
    catch (error) {
      console.error('SM4-CBC解密失败', error)
      throw new Error('SM4-CBC解密失败')
    }
  }

  /**
   * SM4-CBC加密
   * @param data 待加密的字符串
   * @param key 密钥（十六进制字符串）
   * @param iv IV（十六进制字符串）
   * @returns 加密结果（16进制字符串）
   */
  public static encrypt(data: string | Uint8Array, key: string, iv: string): string | Uint8Array {
    try {
      return sm4.encrypt(data, key, {
        iv,
        mode: 'cbc',
        output: 'array',
        padding: 'pkcs#5',
      })
    }
    catch (error) {
      console.error('SM4-CBC加密失败', error)
      throw new Error('SM4-CBC加密失败')
    }
  }

  /**
   * SM4-CBC解密
   * @param encryptedData 加密数据（16进制字符串）
   * @param key 密钥（十六进制字符串）
   * @param iv IV（十六进制字符串）
   * @returns 解密后的字符串
   */
  public static decrypt(encryptedData: string, key: string, iv: string): string {
    try {
      return sm4.decrypt(encryptedData, key, {
        iv,
        mode: 'cbc',
        output: 'string',
      })
    }
    catch (error) {
      console.error('SM4-CBC解密失败', error)
      throw new Error('SM4-CBC解密失败')
    }
  }
}
